import { Injectable, inject } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

export type FeatureMatchMode = 'all' | 'any';

export type FeatureRequirement =
  | string
  | string[]
  | {
      allOf?: string[];
      anyOf?: string[];
      noneOf?: string[];
    };

export type FeatureMenuItem = MenuItem & {
  feature?: FeatureRequirement;
  featureMode?: FeatureMatchMode;
  items?: FeatureMenuItem[];
};

@Injectable({ providedIn: 'root' })
export class FeatureAccessService {
  private readonly _auth = inject(AuthService);

  private readonly featureConfig = environment.keycloak.featureFlags ?? {
    claimPaths: [
      'features',
      'feature_flags',
      'attributes.features',
      'attributes.feature_flags',
      'app_features',
    ],
    groupsClaimPath: 'groups',
    groupPrefixes: [
      'feature:',
      '/feature/',
      'feature/',
      'features:',
      '/features/',
      'features/',
    ],
  };

  has(requirement: FeatureRequirement, mode: FeatureMatchMode = 'all'): boolean {
    const currentFeatures = this.getFeatures();
    return this.matchRequirement(currentFeatures, requirement, mode);
  }

  hasAny(features: string[]): boolean {
    return this.has(features, 'any');
  }

  hasAll(features: string[]): boolean {
    return this.has(features, 'all');
  }

  getFeatures(): Set<string> {
    const decoded = this._auth.getDecodedToken() as Record<string, unknown> | null;
    if (!decoded) return new Set();

    const features = new Set<string>();

    for (const path of this.featureConfig.claimPaths ?? []) {
      const rawValue = this.readPath(decoded, path);
      this.collectFeatures(features, rawValue);
    }

    const groupsRaw = this.readPath(
      decoded,
      this.featureConfig.groupsClaimPath ?? 'groups',
    );
    this.collectFeaturesFromGroups(features, groupsRaw);

    return features;
  }

  filterMenuItems<T extends FeatureMenuItem>(items: T[]): T[] {
    return items
      .map((item) => this.mapMenuItem(item))
      .filter((item): item is T => item !== null);
  }

  private mapMenuItem<T extends FeatureMenuItem>(item: T): T | null {
    if (item.visible === false) {
      return null;
    }

    if (item.feature && !this.has(item.feature, item.featureMode ?? 'all')) {
      return null;
    }

    const mappedItems = item.items
      ? this.filterMenuItems(item.items as T[])
      : undefined;

    const hadChildren = Array.isArray(item.items) && item.items.length > 0;
    const hasAction = !!(item.command || item.routerLink || item.url);

    if (hadChildren && (mappedItems?.length ?? 0) === 0 && !hasAction) {
      return null;
    }

    return {
      ...item,
      ...(mappedItems ? { items: mappedItems } : {}),
    } as T;
  }

  private matchRequirement(
    currentFeatures: Set<string>,
    requirement: FeatureRequirement,
    mode: FeatureMatchMode,
  ): boolean {
    if (typeof requirement === 'string') {
      return currentFeatures.has(this.normalizeFeature(requirement));
    }

    if (Array.isArray(requirement)) {
      const normalized = requirement.map((f) => this.normalizeFeature(f));
      if (!normalized.length) return true;

      return mode === 'any'
        ? normalized.some((f) => currentFeatures.has(f))
        : normalized.every((f) => currentFeatures.has(f));
    }

    const allOf = (requirement.allOf ?? []).map((f) => this.normalizeFeature(f));
    const anyOf = (requirement.anyOf ?? []).map((f) => this.normalizeFeature(f));
    const noneOf = (requirement.noneOf ?? []).map((f) => this.normalizeFeature(f));

    const allOk = allOf.every((f) => currentFeatures.has(f));
    const anyOk = anyOf.length === 0 || anyOf.some((f) => currentFeatures.has(f));
    const noneOk = noneOf.every((f) => !currentFeatures.has(f));

    return allOk && anyOk && noneOk;
  }

  private collectFeatures(target: Set<string>, rawValue: unknown): void {
    if (!rawValue) return;

    if (typeof rawValue === 'string') {
      this.splitString(rawValue).forEach((f) => target.add(this.normalizeFeature(f)));
      return;
    }

    if (Array.isArray(rawValue)) {
      for (const item of rawValue) {
        if (typeof item === 'string') {
          target.add(this.normalizeFeature(item));
        }
      }
      return;
    }

    if (typeof rawValue === 'object') {
      for (const [key, value] of Object.entries(rawValue as Record<string, unknown>)) {
        if (value === true || value === 1 || value === 'true' || value === '1') {
          target.add(this.normalizeFeature(key));
        }
      }
    }
  }

  private collectFeaturesFromGroups(target: Set<string>, groupsRaw: unknown): void {
    if (!Array.isArray(groupsRaw)) return;

    const prefixes = (this.featureConfig.groupPrefixes ?? []).map((p) => p.toLowerCase());

    for (const group of groupsRaw) {
      if (typeof group !== 'string') continue;
      const normalizedGroup = group.trim().toLowerCase();

      const matchedPrefix = prefixes.find((prefix) =>
        normalizedGroup.startsWith(prefix),
      );

      if (!matchedPrefix) continue;

      const feature = normalizedGroup.slice(matchedPrefix.length).trim();
      if (feature) {
        target.add(this.normalizeFeature(feature));
      }
    }
  }

  private splitString(raw: string): string[] {
    return raw
      .split(/[;,\s]+/)
      .map((v) => v.trim())
      .filter(Boolean);
  }

  private normalizeFeature(value: string): string {
    return value.trim().toLowerCase();
  }

  private readPath(source: Record<string, unknown>, path: string): unknown {
    if (!path) return undefined;

    return path
      .split('.')
      .map((key) => key.trim())
      .filter(Boolean)
      .reduce<unknown>((acc, key) => {
        if (!acc || typeof acc !== 'object') {
          return undefined;
        }

        return (acc as Record<string, unknown>)[key];
      }, source);
  }
}
